import { Navigation, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Bottom-left toggle button — enables/disables visitor GPS. Shows a status
 * pill with live accuracy or a helpful error when the browser blocks
 * geolocation. Compact enough to sit above the mobile bottom-nav.
 */
export default function GPSLocateButton({ gps }) {
  const { t } = useTranslation();
  if (!gps.supported) return null;

  const { enabled, worldPos, accuracyMeters, error, enable, disable } = gps;
  const acc = accuracyMeters != null ? Math.round(accuracyMeters) : null;
  const onSite = !!worldPos;

  const statusText = !enabled
    ? t('gps.show_location')
    : error
      ? `⚠ ${error}`
      : onSite
        ? `${t('gps.on_site')} · ${t('gps.accuracy', { m: acc })}`
        : (acc != null ? `${t('gps.locating')} (${t('gps.off_site')}?)` : t('gps.locating'));

  return (
    <div
      className="fixed bottom-6 left-6 z-[45] flex items-center gap-2"
      data-testid="gps-locate-widget"
    >
      <button
        onClick={enabled ? disable : enable}
        className={`group flex items-center gap-2 px-3.5 py-2 rounded-full shadow-lg font-bold text-xs uppercase tracking-widest transition-all border-2 ${
          enabled
            ? onSite
              ? 'bg-sky-500 text-white border-sky-300 hover:bg-sky-400'
              : 'bg-amber-500 text-white border-amber-300 hover:bg-amber-400'
            : 'bg-slate-900/80 text-slate-100 border-slate-700 hover:bg-slate-800 backdrop-blur'
        }`}
        title={enabled ? 'Turn off GPS tracking' : 'Enable outdoor GPS tracking'}
        data-testid="gps-toggle-btn"
      >
        {enabled && !onSite && acc == null ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Navigation
            className={`w-4 h-4 ${enabled && onSite ? 'text-white' : ''}`}
            fill={enabled && onSite ? 'currentColor' : 'none'}
          />
        )}
        <span>{statusText}</span>
      </button>
    </div>
  );
}
